import type { AuthProvider, AuthSession } from './authProvider'
import { APP_SETTINGS } from '../config/appSettings'

type RestorationSource = 'initial' | 'resume'

interface AuthSessionRestorationOptions {
  windowTarget?: EventTarget
  documentTarget?: EventTarget & { visibilityState: DocumentVisibilityState }
  onSession?: (session: AuthSession | null, source: RestorationSource) => void
  onError?: (error: unknown, source: RestorationSource) => void
  setTimeout?: (callback: () => void, delay: number) => number
  clearTimeout?: (timer: number) => void
  sessionTimeoutMs?: number
}

export interface AuthSessionRestorationLifecycle {
  initialCheck: Promise<AuthSession | null>
  refresh: () => Promise<AuthSession | null>
  dispose: () => void
}

export function startAuthSessionRestoration(
  provider: Pick<AuthProvider, 'getSession'>,
  options: AuthSessionRestorationOptions = {},
): AuthSessionRestorationLifecycle {
  const windowTarget = options.windowTarget ?? window
  const documentTarget = options.documentTarget ?? document
  const scheduleTimeout = options.setTimeout ?? globalThis.setTimeout.bind(globalThis)
  const cancelTimeout = options.clearTimeout ?? globalThis.clearTimeout.bind(globalThis)
  const sessionTimeoutMs = options.sessionTimeoutMs ?? APP_SETTINGS.auth.sessionRestorationTimeoutMs
  let disposed = false
  let activeCheck: Promise<AuthSession | null> | null = null

  const restore = (source: RestorationSource): Promise<AuthSession | null> => {
    if (disposed) return Promise.resolve(null)
    if (activeCheck) return activeCheck
    let timeout: number | null = null
    const timeoutPromise = new Promise<never>((_resolve, reject) => {
      timeout = scheduleTimeout(
        () => reject(new Error('Session restoration timed out.')),
        sessionTimeoutMs,
      )
    })
    activeCheck = Promise.race([provider.getSession(), timeoutPromise])
      .then((session) => {
        if (!disposed) options.onSession?.(session, source)
        return session
      })
      .catch((error: unknown) => {
        if (!disposed) options.onError?.(error, source)
        return null
      })
      .finally(() => {
        if (timeout !== null) cancelTimeout(timeout)
        activeCheck = null
      })
    return activeCheck
  }

  const refreshWhenVisible = () => {
    if (documentTarget.visibilityState !== 'visible') return
    void restore('resume')
  }
  windowTarget.addEventListener('online', refreshWhenVisible)
  windowTarget.addEventListener('focus', refreshWhenVisible)
  windowTarget.addEventListener('pageshow', refreshWhenVisible)
  documentTarget.addEventListener('visibilitychange', refreshWhenVisible)

  const initialCheck = restore('initial')
  return {
    initialCheck,
    refresh: () => restore('resume'),
    dispose: () => {
      disposed = true
      windowTarget.removeEventListener('online', refreshWhenVisible)
      windowTarget.removeEventListener('focus', refreshWhenVisible)
      windowTarget.removeEventListener('pageshow', refreshWhenVisible)
      documentTarget.removeEventListener('visibilitychange', refreshWhenVisible)
    },
  }
}
